<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'

import type { OnboardingConnectionResult } from '../../models/onboardingConnection.types'

import { useWorkspace } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { Alert, Spinner } from '@/shared/ui'

// Lazy, not a static import (ORP-22679): this component is itself statically re-exported from
// the barrel (`export { default as OnboardingConnectorSurface } from ...`, consumed by module 29's
// onboarding wizard), so a plain `import ConnectionsPage from '../ConnectionsPage.vue'` here would
// drag ConnectionsPage.vue's whole module back into the SPA static entry closure regardless of the
// barrel's own lazy `ConnectionsPage` export.
const ConnectionsPage = defineAsyncComponent(() => import('../ConnectionsPage.vue'))

const props = defineProps<{ workspaceId: number | string }>()

const emit = defineEmits<{ connected: [result: OnboardingConnectionResult] }>()
const workspace = useWorkspace()
const { t } = useI18n()

// Connector APIs bind to the selected workspace. Refuse to render against a different id instead
// of letting an onboarding host accidentally register accounts on the previously selected context.
const contextState = computed<'loading' | 'error' | 'ready'>(() => {
  if (workspace.workspaceId.value === undefined) return 'loading'
  return String(workspace.workspaceId.value) === String(props.workspaceId) ? 'ready' : 'error'
})
</script>

<template>
  <section
    class="onboarding-connector-surface min-w-0"
    data-testid="onboarding-connector-surface"
    :data-workspace-id="workspaceId"
  >
    <!-- ConnectionsPage already owns the auth-family list, provider grid, account-picker adapters,
         registration flow and loading/empty/error/ready projection. Forward its post-refresh event;
         provider follow-ups continue to consume the existing app-owned bus exactly once. -->
    <div
      v-if="contextState === 'loading'"
      class="onboarding-connector-surface__state grid min-h-32 place-items-center"
      data-testid="onboarding-connector-loading"
    >
      <Spinner>{{ t('integrations.connections.onboardingContextLoading') }}</Spinner>
    </div>
    <Alert
      v-else-if="contextState === 'error'"
      tone="danger"
      :title="t('integrations.connections.onboardingContextMismatch')"
      data-testid="onboarding-connector-error"
    />
    <ConnectionsPage v-else @connected="emit('connected', $event)" />
  </section>
</template>
